/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
export const intervalColors: { [key: string]: string } = {
  // Interval Colors
  'I': '#00FF00',    // Root - Bright Green (major or minor)
  'm2': '#8E44AD',   // Minor 2nd 
  'M2': '#FFD700',   // Major 2nd - Changed to Gold
  'm3': '#A8DADC',   // Minor 3rd - Soft Light Blue
  'M3': '#457B9D',   // Major 3rd - Deep Blue
  'P4': '#990000',   // Perfect 4th - Charcoal Gray
  'TRI': '#F77F00',  // Tritone - Orange
  'P5': '#2A9D8F',   // Perfect 5th - Teal
  'm6': '#C9ADA7',   // Minor 6th - Beige
  'M6': '#FFA07A',   // Major 6th - Changed to Light Salmon
  'm7': '#264653',   // Minor 7th - Dark Green
  'M7': '#9B59B6',   // Major 7th - Purple

  // Roman Numerals (Major and Minor)
  'i': '#00FF00',    // Root (minor) - Bright Green
  'bII': '#8E44AD',  // Flat second - Same as m2
  'II': '#FFD700',   // Major second - Same as new M2
  'bIII': '#A8DADC', // Flat third - Same as m3
  'III': '#457B9D',  // Major third - Same as M3
  'IV': '#990000',   // Perfect fourth - Same as P4
  '#IV': '#F77F00',  // Augmented fourth - Same as TRI
  'bV': '#F77F00',   // Diminished fifth - Same as TRI
  'V': '#2A9D8F',    // Perfect fifth - Same as P5
  'bVI': '#C9ADA7',  // Minor sixth - Same as m6
  'VI': '#FFA07A',   // Major sixth - Same as new M6
  'bVII': '#264653', // Minor seventh - Same as m7
  'VII': '#9B59B6',  // Major seventh - Same as M7

  // Additional for completeness
  'ii': '#8E44AD',   // Supertonic (minor) - Same as m2
  'iii': '#A8DADC',  // Mediant (minor) - Same as m3
  'iv': '#990000',   // Subdominant (minor) - Same as P4
  'v': '#2A9D8F',    // Dominant (minor) - Same as P5
  'vi': '#C9ADA7',   // Submediant (minor) - Same as m6
  'vii': '#264653',  // Leading tone (minor) - Same as m7
  'vii°': '#F77F00', // Diminished seventh - Same as TRI

  // Augmented and diminished chords
  'I+': '#F77F00',   // Augmented tonic - Same as TRI
  'ii°': '#F77F00',  // Diminished supertonic - Same as TRI
  'iii°': '#F77F00', // Diminished mediant - Same as TRI
  'IV+': '#F77F00',  // Augmented subdominant - Same as TRI
  'V+': '#F77F00',   // Augmented dominant - Same as TRI
  'vi°': '#F77F00',  // Diminished submediant - Same as TRI
};

export const getIntervalColor = (identifier: string): string => {
  return intervalColors[identifier] || '#000000'; // Default color if not found
};

/**
 * Simplified color scheme for beginners.
 * Only distinguishes Root from other scale notes.
 */
export const BEGINNER_COLORS = {
  root: '#00FF00',      // Bright Green - same as advanced root
  scaleTone: '#6B9BD1', // Soft Blue - calming, educational feel
};

/**
 * Get interval color based on skill level.
 * - Beginner: Root is green, everything else is gray
 * - Intermediate/Advanced: Full interval color palette
 */
export const getIntervalColorForSkillLevel = (
  identifier: string,
  useSimplifiedColors: boolean
): string => {
  if (!useSimplifiedColors) {
    return getIntervalColor(identifier);
  }

  // Beginner mode: Root is green, everything else is gray
  const isRoot = identifier === 'I' || identifier === 'i';
  return isRoot ? BEGINNER_COLORS.root : BEGINNER_COLORS.scaleTone;
};


export const getFingeringColor = (fingerNumber?: number): string => {
  const fingerColors: { [key: number]: string } = {
    1: '#FF6B6B', // Index finger - Red
    2: '#4ECDC4', // Middle finger - Teal  
    3: '#45B7D1', // Ring finger - Blue
    4: '#96CEB4', // Pinky - Green
  };
  
  return fingerNumber ? fingerColors[fingerNumber] || '#DDD' : '#DDD';
};


export const getChordTypeColor = (type: 'scale' | 'chord' | 'diatonic' | 'fingering'): string => {
  const typeColors = {
    scale: '#4A90E2',      // Blue for scales
    chord: '#7ED321',      // Green for chords
    diatonic: '#F5A623',   // Orange for diatonic chords
    fingering: '#9013FE'   // Purple for fingerings
  };
  
  return typeColors[type];
};
